export const ALL_STAFF_ROLES = [
  'Admin',
  'Cashier',
  'SalesStaff',
  'ProcurementLead',
  'ProcurementStaff',
  'OnlineSalesLead',
  'OnlineSalesStaff',
  'Mechanic',
  'ServiceAdvisor',
  'WarehouseLead',
  'WarehouseStaff',
  'AccountingStaff',
  'AccountingLead',
  'HumanResourceStaff'
] as const;

export type StaffRole = (typeof ALL_STAFF_ROLES)[number];

export const ROLE_OPTIONS: { value: StaffRole; label: string }[] = [
  { value: 'Cashier', label: 'Cashier' },
  { value: 'SalesStaff', label: 'Sales Staff' },
  { value: 'ProcurementLead', label: 'Procurement Lead' },
  { value: 'ProcurementStaff', label: 'Procurement Staff' },
  { value: 'OnlineSalesLead', label: 'OnlineSales Lead' },
  { value: 'OnlineSalesStaff', label: 'OnlineSales Staff' },
  { value: 'Mechanic', label: 'Mechanic' },
  { value: 'ServiceAdvisor', label: 'Service Advisor' },
  { value: 'WarehouseLead', label: 'Warehouse Lead' },
  { value: 'WarehouseStaff', label: 'Warehouse Staff' },
  { value: 'AccountingStaff', label: 'Accounting Staff' },
  { value: 'AccountingLead', label: 'Accounting Lead' },
  { value: 'HumanResourceStaff', label: 'Human Resource Staff' },
  { value: 'Admin', label: 'Admin' }
];

/** Feature access by role. Admin is included on every feature. */
export const FeatureRoles = {
  inventorySearch: [...ALL_STAFF_ROLES],
  aiQuery: [...ALL_STAFF_ROLES],
  actualQuantity: [
    'Admin',
    'WarehouseLead',
    'WarehouseStaff',
    'ProcurementLead',
    'ProcurementStaff'
  ],
  gate: [...ALL_STAFF_ROLES],
  activityLog: [...ALL_STAFF_ROLES],
  aiCallLogs: ['Admin'],
  productSearchLogs: ['Admin'],
  salesInvoice: ['Admin', 'Cashier', 'SalesStaff', 'AccountingStaff', 'AccountingLead'],
  newSaleTransaction: ['Admin', 'Cashier'],
  products: [
    'Admin',
    'SalesStaff',
    'ProcurementStaff',
    'ProcurementLead',
    'ServiceAdvisor',
    'Mechanic'
  ],
  productCategoriesBrands: ['Admin', 'ProcurementStaff', 'ProcurementLead'],
  purchaseOrder: ['Admin', 'ProcurementStaff', 'ProcurementLead', 'SalesStaff'],
  resupply: [
    'Admin',
    'ProcurementStaff',
    'ProcurementLead',
    'SalesStaff',
    'ServiceAdvisor'
  ],
  purchaseOrderApprove: ['Admin', 'ProcurementLead'],
  purchaseOrderClose: ['Admin', 'ProcurementStaff', 'ProcurementLead'],
  purchaseOrderDelete: ['Admin'],
  purchaseOrderFulfillment: ['Admin', 'ProcurementStaff', 'ProcurementLead'],
  procurementInvoice: [
    'Admin',
    'ProcurementStaff',
    'ProcurementLead',
    'AccountingStaff',
    'AccountingLead'
  ],
  recentlyApprovedPackagePhotos: [
    'Admin',
    'Cashier',
    'SalesStaff',
    'OnlineSalesStaff',
    'OnlineSalesLead',
    'ProcurementStaff',
    'ProcurementLead',
    'AccountingStaff',
    'AccountingLead'
  ],
  returns: [
    'Admin',
    'SalesStaff',
    'ProcurementStaff',
    'ProcurementLead',
    'AccountingStaff',
    'AccountingLead'
  ],
  vehicles: ['Admin', 'ServiceAdvisor', 'Mechanic', 'AccountingStaff', 'AccountingLead'],
  shopServiceCreate: ['Admin', 'ServiceAdvisor'],
  customers: [
    'Admin',
    'Cashier',
    'SalesStaff',
    'ServiceAdvisor',
    'AccountingStaff',
    'AccountingLead'
  ],
  supplierPayments: ['Admin', 'AccountingStaff', 'AccountingLead'],
  expenses: ['Admin', 'AccountingStaff', 'AccountingLead'],
  supplierDetails: [
    'Admin',
    'ProcurementStaff',
    'ProcurementLead',
    'SalesStaff',
    'AccountingStaff',
    'AccountingLead'
  ],
  salesReport: ['Admin'],
  vehicleRepairReport: ['Admin'],
  procurementSummary: ['Admin', 'AccountingLead'],
  timelog: ['Admin', 'HumanResourceStaff'],
  humanResources: ['Admin', 'HumanResourceStaff'],
  thirteenthMonth: ['Admin', 'HumanResourceStaff', 'AccountingLead'],
  adminOnly: ['Admin'],
  tasks: [...ALL_STAFF_ROLES]
} as const;

export const hasAnyRole = (
  userRoles: Array<{ item2?: string } | string> | undefined,
  allowedRoles: readonly string[]
): boolean => {
  if (!userRoles?.length) return false;
  return userRoles.some(role => {
    const roleName = typeof role === 'string' ? role : role.item2;
    return !!roleName && allowedRoles.includes(roleName);
  });
};
